import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useParams } from "react-router-dom";
import { API_LoginURL, Img_APIURL } from "../../config";
import SideBar from "../Dashboard/StudentDashboard/Sidebar/SideBar";
import Breadcrumb from "../StudentBreadcrumb/Breadcrumb";

const ProjectDetail = () => {
    const { id } = useParams();
    const [submittedProjects, setSubmittedProjects] = useState([]);
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchSubmittedProjects();
    }, []);

    const fetchSubmittedProjects = async () => {
        try {
            const response = await axios.get(
                `${API_LoginURL}project/ProjectSubmissionListById`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: localStorage.getItem("token"),
                    },
                }
            );
            setSubmittedProjects(response.data);
        } catch (error) {
            console.error("Error fetching submitted projects:", error);
            setError("Error fetching projects");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (submittedProjects.length > 0) {
            const foundProject = submittedProjects.find(
                (project) => project.projectSubmissionId.toString() === id
            );
            setProject(foundProject);
        }
    }, [submittedProjects, id]);

    if (loading) {
        return <Typography>Loading...</Typography>;
    }

    if (error) {
        return <Typography>{error}</Typography>;
    }

    if (!project) {
        return <Typography>No project found with this ID</Typography>;
    }

    const handleDownload = (filePath) => {
        const link = document.createElement("a");
        link.href = filePath;
        link.download = filePath.split("/").pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderDocumentViewer = (filePath) => {
        if (filePath.endsWith(".pdf")) {
            return (
                <iframe
                    src={filePath}
                    width="100%"
                    height="500px"
                    title="PDF Document"
                />
            );
        } else if (filePath.endsWith(".docx") || filePath.endsWith(".doc")) {
            // Use Google Docs Viewer for DOCX files
            const googleDocsViewerUrl = `https://docs.google.com/gview?url=${filePath}&embedded=true`;
            return (
                <iframe
                    src={googleDocsViewerUrl}
                    width="100%"
                    height="500px"
                    title="Document Viewer"
                />
            );
        } else if (filePath.endsWith(".txt")) {
            return (
                <pre>
                    {`Document content is not displayed here for text files.`}
                </pre>
            );
        } else {
            return (
                <Typography>
                    No document viewer available for this file type.
                </Typography>
            );
        }
    };

    return (
        <>
            <SideBar />
            <Box
                sx={{
                    flexGrow: 1,
                    position: "relative",
                    width: "calc(100vw - 60px)",
                    padding: "20px",
                    minHeight: "100vh",
                    left: "60px",
                }}
            >
                <Breadcrumb />
                <Stack spacing={2}>
                    <Box sx={{ padding: 4 }}>
                        <Typography variant="h4" sx={{ mb: 2 }}>
                            {project.projectName}
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Summary:
                                </Typography>
                                <Typography variant="body1">
                                    {project.projectSummary}
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Media:
                                </Typography>
                                <Box>
                                    {project.mediaFiles &&
                                    project.mediaFiles.length > 0 ? (
                                        <Carousel>
                                            {project.mediaFiles.map(
                                                (media, index) => (
                                                    <div key={index}>
                                                        {media.projectMediaPath?.endsWith(
                                                            ".mp4"
                                                        ) ? (
                                                            <video
                                                                src={
                                                                    Img_APIURL +
                                                                    media.projectMediaPath
                                                                }
                                                                style={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    objectFit:
                                                                        "cover",
                                                                }}
                                                                controls
                                                                autoPlay
                                                            />
                                                        ) : (
                                                            <img
                                                                src={
                                                                    media.projectMediaPath
                                                                        ? Img_APIURL +
                                                                          media.projectMediaPath
                                                                        : "https://via.placeholder.com/100"
                                                                }
                                                                alt="Thumbnail"
                                                                style={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    objectFit:
                                                                        "cover",
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                )
                                            )}
                                        </Carousel>
                                    ) : (
                                        <Typography>
                                            No media files available
                                        </Typography>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Submitted Documents:
                                </Typography>
                                <Box>
                                    {project.mediaFiles.map((media, index) => (
                                        <Box key={index} sx={{ mb: 2 }}>
                                            <Typography
                                                variant="body1"
                                                sx={{ mb: 1 }}
                                            >
                                                Document {index + 1}:{" "}
                                                {media.projectFilePath
                                                    .split("/")
                                                    .pop()}
                                            </Typography>
                                            {media.projectFilePath &&
                                                renderDocumentViewer(
                                                    Img_APIURL +
                                                        media.projectFilePath
                                                )}
                                            <Button
                                                variant="contained"
                                                sx={{ mt: 2 }}
                                                onClick={() =>
                                                    handleDownload(
                                                        Img_APIURL +
                                                            media.projectFilePath
                                                    )
                                                }
                                            >
                                                Download Document
                                            </Button>
                                        </Box>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Submission Date:
                                </Typography>
                                <Typography variant="body1">
                                    {project.projectSubmissionDate}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>
                </Stack>
            </Box>
        </>
    );
};

export default ProjectDetail;
